import { z } from "zod";

/**
 * O contrato do stream canônico. Toda fonte — Health Connect, Apple Health,
 * Strava, foto, código de barras, digitação — normaliza para uma `Observation`.
 * Fonte nova entra sem migration de banco.
 */

export const observationTypeSchema = z.enum([
  "weight",
  "body_fat",
  "lean_mass",
  "steps",
  "hr_resting",
  "hrv",
  "sleep",
  "workout",
  "meal",
  "supplement_dose",
  "medication_dose",
  "blood_pressure",
  "glucose",
  "mood",
  "note",
  "lab_result",
  "vo2max",
  "water",
]);
export type ObservationType = z.infer<typeof observationTypeSchema>;

export const observationSourceSchema = z.enum([
  "health_connect",
  "apple_health",
  "strava",
  "manual",
  "photo",
  "barcode",
  "import",
  "agent",
]);
export type ObservationSource = z.infer<typeof observationSourceSchema>;

/** Confiança do dado. É o que separa "tu comeu" de "eu chutei". */
export const CONFIDENCE = {
  /** Digitado pela pessoa ou medido por dispositivo. */
  measured: 1,
  /** Derivado de um plano prescrito, sem confirmação. */
  prescribed: 0.7,
  /** Estimado por visão computacional a partir de foto. */
  photo: 0.5,
  /** Chute do agente a partir de contexto. */
  inferred: 0.3,
} as const;

export const sleepPayloadSchema = z.object({
  totalMinutes: z.number().min(0).max(1440),
  deepMinutes: z.number().min(0).max(1440).optional(),
  remMinutes: z.number().min(0).max(1440).optional(),
  lightMinutes: z.number().min(0).max(1440).optional(),
  awakeMinutes: z.number().min(0).max(1440).optional(),
  efficiency: z.number().min(0).max(1).optional(),
  bedtime: z.string().datetime().optional(),
  wakeTime: z.string().datetime().optional(),
});

export const workoutPayloadSchema = z.object({
  activity: z.string().min(1),
  durationMinutes: z.number().min(0),
  distanceMeters: z.number().min(0).optional(),
  activeKcal: z.number().min(0).optional(),
  avgHr: z.number().min(20).max(240).optional(),
  maxHr: z.number().min(20).max(250).optional(),
  elevationGainM: z.number().optional(),
  /** Volume por grupo muscular, para o motor de fadiga. */
  groupVolume: z.record(z.string(), z.number()).optional(),
  externalUrl: z.string().url().optional(),
});

export const mealPayloadSchema = z.object({
  mealId: z.string(),
  slot: z.enum(["cafe", "lanche_manha", "almoco", "lanche_tarde", "jantar", "ceia", "avulso"]),
  kcal: z.number().min(0),
  proteinG: z.number().min(0),
  carbG: z.number().min(0),
  fatG: z.number().min(0),
  itemCount: z.number().int().min(0),
  photoKey: z.string().optional(),
});

export const dosePayloadSchema = z.object({
  substanceId: z.string(),
  substanceName: z.string(),
  amount: z.number().min(0),
  unit: z.string(),
  regimenId: z.string().optional(),
  scheduledFor: z.string().datetime().optional(),
  /** `taken` só quando a pessoa confirmou. `skipped` é informação, não ausência. */
  status: z.enum(["taken", "skipped", "missed"]),
});

export const bloodPressurePayloadSchema = z.object({
  systolic: z.number().min(50).max(300),
  diastolic: z.number().min(30).max(200),
  pulse: z.number().min(20).max(240).optional(),
  position: z.enum(["sentado", "deitado", "em_pe"]).optional(),
});

export const labPayloadSchema = z.object({
  analyte: z.string(),
  value: z.number(),
  unit: z.string(),
  refLow: z.number().optional(),
  refHigh: z.number().optional(),
  lab: z.string().optional(),
  collectedAt: z.string().datetime().optional(),
});

export const moodPayloadSchema = z.object({
  /** 1..5, onde 3 é neutro. */
  mood: z.number().int().min(1).max(5),
  energy: z.number().int().min(1).max(5).optional(),
  stress: z.number().int().min(1).max(5).optional(),
  soreness: z.number().int().min(1).max(5).optional(),
  tags: z.array(z.string()).optional(),
  note: z.string().max(2000).optional(),
});

export const observationSchema = z.object({
  id: z.string(),
  ownerId: z.string(),
  ts: z.string().datetime(),
  tz: z.string(),
  type: observationTypeSchema,
  value: z.number().nullable(),
  unit: z.string().nullable(),
  payload: z.unknown().nullable(),
  source: observationSourceSchema,
  sourceId: z.string().nullable(),
  /** Quem registrou. Diferente de `ownerId` quando veio de um grant `log_for`. */
  loggedBy: z.string(),
  confidence: z.number().min(0).max(1),
  supersededBy: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type Observation = z.infer<typeof observationSchema>;

/** O que a borda de ingestão aceita. `id`, `loggedBy` e `createdAt` são do servidor. */
export const observationInputSchema = observationSchema
  .omit({ id: true, loggedBy: true, createdAt: true, supersededBy: true })
  .partial({ ownerId: true, tz: true, value: true, unit: true, payload: true, sourceId: true, confidence: true });
export type ObservationInput = z.infer<typeof observationInputSchema>;

/** Payload validado por tipo. Devolve `null` quando o tipo não tem payload estruturado. */
export function parsePayload(type: ObservationType, payload: unknown): unknown {
  switch (type) {
    case "sleep":
      return sleepPayloadSchema.parse(payload);
    case "workout":
      return workoutPayloadSchema.parse(payload);
    case "meal":
      return mealPayloadSchema.parse(payload);
    case "supplement_dose":
    case "medication_dose":
      return dosePayloadSchema.parse(payload);
    case "blood_pressure":
      return bloodPressurePayloadSchema.parse(payload);
    case "lab_result":
      return labPayloadSchema.parse(payload);
    case "mood":
      return moodPayloadSchema.parse(payload);
    default:
      return payload ?? null;
  }
}

export const shareScopeSchema = z.enum([
  "meal",
  "weight",
  "body_comp",
  "workout",
  "sleep",
  "hrv",
  "medication",
  "supplement",
  "labs",
  "mood",
  "protocol",
  "adherence",
  "cycle",
]);
export type ShareScope = z.infer<typeof shareScopeSchema>;

export const shareLevelSchema = z.enum(["none", "summary", "read", "log_for"]);
export type ShareLevel = z.infer<typeof shareLevelSchema>;

export const SHARE_LEVEL_RANK: Record<ShareLevel, number> = {
  none: 0,
  summary: 1,
  read: 2,
  log_for: 3,
};

/** Quais tipos de observação cada escopo de compartilhamento cobre. */
export const SCOPE_TO_TYPES: Record<ShareScope, readonly ObservationType[]> = {
  meal: ["meal", "water"],
  weight: ["weight"],
  body_comp: ["body_fat", "lean_mass"],
  workout: ["workout", "steps", "vo2max"],
  sleep: ["sleep"],
  hrv: ["hrv", "hr_resting"],
  medication: ["medication_dose"],
  supplement: ["supplement_dose"],
  labs: ["lab_result", "blood_pressure", "glucose"],
  mood: ["mood", "note"],
  protocol: [],
  adherence: [],
  cycle: [],
};

export const cycleFocusSchema = z.enum([
  "body_composition",
  "performance",
  "clinical",
  "consistency",
]);
export type CycleFocus = z.infer<typeof cycleFocusSchema>;
